import React from 'react';

export interface ProgressBarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  /** Current value */
  value?: number;
  /** Maximum value */
  max?: number;
  /** Minimum value */
  min?: number;
  /** Show value as percentage or absolute value */
  showValue?: boolean | 'percentage' | 'absolute';
  /** Whether to show label */
  showLabel?: boolean;
  /** Custom label text (defaults to percentage) */
  label?: string;
  /** Height of the progress bar */
  height?: 'xs' | 'sm' | 'md' | 'lg';
  /** Color variant */
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';
  /** Color string - can be a named color like 'green' or custom variant name */
  color?: string;
  /** Show/hide stripe animation */
  striped?: boolean;
  /** Show/hide animation */
  animated?: boolean;
  /** Whether the progress bar is indeterminate */
  indeterminate?: boolean;
  /** Border radius */
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'full';
}

export const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      value,
      max = 100,
      min = 0,
      showValue = false,
      showLabel = false,
      label,
      height = 'md',
      variant = 'primary',
      color,
      striped = false,
      animated = false,
      indeterminate = false,
      radius = 'sm',
      className = '',
      ...props
    },
    ref
  ) => {
    // Calculate percentage
    const range = max - min;
    const valueInRange = value !== undefined ? Math.min(Math.max(value, min), max) : min;
    const percentage = ((valueInRange - min) / range) * 100;
    
    // Format displayed value
    const getDisplayValue = () => {
      if (showValue === true || showValue === 'percentage') {
        return `${Math.round(percentage)}%`;
      }
      if (showValue === 'absolute') {
        return `${value} / ${max}`;
      }
      return null;
    };
    
    // Height classes
    const heightClasses = {
      xs: 'h-1',
      sm: 'h-2',
      md: 'h-3',
      lg: 'h-4',
    };
    
    // Variant classes for the filled part
    const variantClasses = {
      primary: 'bg-blue-500 dark:bg-blue-600',
      secondary: 'bg-purple-500 dark:bg-purple-600',
      success: 'bg-green-500 dark:bg-green-600',
      warning: 'bg-yellow-500 dark:bg-yellow-600',
      danger: 'bg-red-500 dark:bg-red-600',
      info: 'bg-indigo-500 dark:bg-indigo-600',
    };
    
    // Border radius classes
    const radiusClasses = {
      none: 'rounded-none',
      sm: 'rounded-sm',
      md: 'rounded',
      lg: 'rounded-lg',
      full: 'rounded-full',
    };
    
    return (
      <div
        className={`w-full ${showLabel ? 'mb-4' : ''} ${className}`}
        {...props}
      >
        {/* Optional label */}
        {showLabel && (
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {label || 'Progress'}
            </span>
            {showValue && (
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {getDisplayValue()}
              </span>
            )}
          </div>
        )}
        
        {/* Progress bar container */}
        <div
          ref={ref}
          className={`
            w-full
            bg-gray-200 dark:bg-gray-700
            ${heightClasses[height]}
            ${radiusClasses[radius]}
            overflow-hidden
          `}
        >
          {/* Progress bar fill */}
          <div
            className={`
              ${color ? `bg-${color}-500 dark:bg-${color}-600` : variantClasses[variant]}
              h-full
              transition-width duration-300 ease-in-out
              ${striped ? 'bg-stripes-light dark:bg-stripes-dark' : ''}
              ${animated ? 'animate-progress' : ''}
              ${indeterminate ? 'animate-indeterminate w-3/4' : ''}
            `}
            style={!indeterminate ? { width: `${percentage}%` } : undefined}
            role="progressbar"
            aria-valuenow={!indeterminate ? value : undefined}
            aria-valuemin={min}
            aria-valuemax={max}
          >
            {showValue && !showLabel && !indeterminate && (
              <div className="flex items-center justify-center h-full">
                <span className={`text-xs font-medium text-white ${height === 'xs' || height === 'sm' ? 'hidden' : ''}`}>
                  {getDisplayValue()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';

export default ProgressBar;